type MessageType = string | number
type GoogleMessage = {
  proceed: (allow: boolean, messageTypes?: MessageType[]) => void
}
type MessagingFunction = (message: GoogleMessage) => void | Promise<void>
type PrivacyDecision = { allow: boolean; types?: MessageType[] }

declare global {
  interface Window {
    googlefc?: {
      controlledMessagingFunction?: MessagingFunction
      MessageTypeEnum?: { OFFERWALL?: MessageType }
    }
  }
}

const OPT_IN_KEY = 'haber:adsense-offerwall:optin'
const OPT_IN_MAX_AGE_MS = 90_000
const MESSAGING_TIMEOUT_MS = 8_000
const SCRIPT_ID = 'adsbygoogle-js'
const pendingDecisions = new Set<() => void>()
let installedFunction: MessagingFunction | undefined
let eligibility: boolean | null = false
let reloading = false
let attemptRejected = false

function currentRoute(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.pathname + window.location.search
}

function consumeAttempt(): string | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(OPT_IN_KEY)
    if (raw === null) return null
    // Remove before parsing: failed validation and a second reload cannot opt in.
    window.sessionStorage.removeItem(OPT_IN_KEY)
    if (window.sessionStorage.getItem(OPT_IN_KEY) !== null) return null
    const marker = JSON.parse(raw) as { route?: unknown; issuedAt?: unknown }
    const age = Date.now() - Number(marker.issuedAt)
    return marker.route === currentRoute() &&
      typeof marker.issuedAt === 'number' &&
      Number.isFinite(age) &&
      age >= 0 &&
      age <= OPT_IN_MAX_AGE_MS
      ? (marker.route as string)
      : null
  } catch {
    return null
  }
}

// Capture on the new document, before either the guard or an article reads it.
// This never becomes true in the document where the CTA was clicked.
let attemptRoute = consumeAttempt()

export function isAdSenseOfferwallAttempt(): boolean {
  if (!attemptRoute) return false
  if (attemptRoute !== currentRoute()) attemptRoute = null
  return attemptRoute !== null
}

/** Explicit user click only. Native messaging needs a fresh page-level decision. */
export function beginAdSenseOfferwall(): boolean {
  if (typeof window === 'undefined' || reloading || isAdSenseOfferwallAttempt())
    return false
  const route = currentRoute()
  if (!route || typeof window.location.reload !== 'function') return false
  try {
    const marker = JSON.stringify({ route, issuedAt: Date.now() })
    window.sessionStorage.setItem(OPT_IN_KEY, marker)
    if (window.sessionStorage.getItem(OPT_IN_KEY) !== marker) {
      window.sessionStorage.removeItem(OPT_IN_KEY)
      return false
    }
    reloading = true
    window.location.reload()
    return true
  } catch {
    reloading = false
    try {
      window.sessionStorage.removeItem(OPT_IN_KEY)
    } catch {
      /* unavailable */
    }
    return false
  }
}

/** null waits briefly for strict config/auth; true requires this page's opt-in. */
export function setAdSenseOfferwallEligibility(value: boolean | null): void {
  eligibility = isAdSenseOfferwallAttempt() && !attemptRejected ? value : false
  for (const flush of [...pendingDecisions]) flush()
}

/** Preserve CMP decisions; suppress only native Offerwall outside a validated click. */
export function suppressNativeGoogleOfferwall(): void {
  if (typeof window === 'undefined') return
  const fc = (window.googlefc = window.googlefc || {})
  if (installedFunction && fc.controlledMessagingFunction === installedFunction)
    return
  const previous = fc.controlledMessagingFunction
  installedFunction = (message) => {
    let settled = false
    let priorDecision: PrivacyDecision | null = previous
      ? null
      : { allow: true }
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const finish = (timedOut = false) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      clearTimeout(retryTimer)
      pendingDecisions.delete(flush)
      if (timedOut) {
        attemptRejected = true
        eligibility = false
      }
      const prior = priorDecision || { allow: true }
      // Missing/stalled prior hooks must never turn into permission for an ad.
      const allowNative =
        !timedOut && eligibility === true && isAdSenseOfferwallAttempt()
      const offerwallType = window.googlefc?.MessageTypeEnum?.OFFERWALL
      if (
        allowNative ||
        (!prior.allow && !prior.types?.length) ||
        offerwallType === undefined
      ) {
        // Unknown SDK enum must not cause global CMP suppression.
        message.proceed(prior.allow, prior.types)
        return
      }
      const blocked = prior.allow
        ? [offerwallType]
        : [...new Set([...(prior.types || []), offerwallType])]
      message.proceed(false, blocked)
    }
    const flush = () => {
      if (settled || !priorDecision) return
      if (!priorDecision.allow && !priorDecision.types?.length) {
        finish()
        return
      }
      if (isAdSenseOfferwallAttempt() && eligibility === null) return
      if (
        (eligibility === true && isAdSenseOfferwallAttempt()) ||
        window.googlefc?.MessageTypeEnum?.OFFERWALL !== undefined
      ) {
        finish()
      } else if (retryTimer === undefined) {
        retryTimer = setTimeout(() => {
          retryTimer = undefined
          flush()
        }, 50)
      }
    }
    const timer = setTimeout(() => finish(true), MESSAGING_TIMEOUT_MS)
    pendingDecisions.add(flush)
    const fallback = () => {
      if (settled || priorDecision) return
      priorDecision = { allow: true }
      flush()
    }
    if (previous) {
      try {
        const result = previous({
          ...message,
          proceed: (allow, types) => {
            if (settled || priorDecision) return
            priorDecision = { allow, types }
            flush()
          },
        })
        void Promise.resolve(result).catch(fallback)
      } catch {
        fallback()
      }
    }
    flush()
  }
  fc.controlledMessagingFunction = installedFunction
}

export function isValidAdSenseOfferwallClient(client: string): boolean {
  return /^ca-pub-\d{16}$/.test(client) && client !== 'ca-pub-0000000000000000'
}

/** Called only after the reloaded document validates the explicit opt-in. */
export function ensureAdSenseOfferwallTag(
  client: string,
  signal?: AbortSignal,
): Promise<boolean> {
  if (
    typeof document === 'undefined' ||
    !isAdSenseOfferwallAttempt() ||
    eligibility !== true ||
    !isValidAdSenseOfferwallClient(client) ||
    signal?.aborted
  ) {
    return Promise.resolve(false)
  }
  return new Promise<boolean>((resolve) => {
    let script = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null
    const owned = !script
    if (script) {
      try {
        const url = new URL(script.src)
        if (
          url.origin !== 'https://pagead2.googlesyndication.com' ||
          url.pathname !== '/pagead/js/adsbygoogle.js' ||
          url.searchParams.get('client') !== client
        ) {
          resolve(false)
          return
        }
      } catch {
        resolve(false)
        return
      }
      if (script.dataset.offerwallStatus === 'loaded') {
        resolve(true)
        return
      }
      if (script.dataset.offerwallStatus === 'error') {
        resolve(false)
        return
      }
    } else {
      script = document.createElement('script')
      script.id = SCRIPT_ID
      script.async = true
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(client)}`
      script.crossOrigin = 'anonymous'
      const nonceSource =
        document.querySelector<HTMLScriptElement>('script[nonce]')
      const nonce = nonceSource?.nonce || nonceSource?.getAttribute('nonce')
      if (nonce) script.nonce = nonce
    }
    const tag = script
    let settled = false
    const finish = (ok: boolean) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      tag.removeEventListener('load', onLoad)
      tag.removeEventListener('error', onError)
      signal?.removeEventListener('abort', onAbort)
      if (!ok && owned) tag.remove()
      resolve(ok)
    }
    const onLoad = () => {
      tag.dataset.offerwallStatus = 'loaded'
      finish(true)
    }
    const onError = () => {
      tag.dataset.offerwallStatus = 'error'
      finish(false)
    }
    const onAbort = () => finish(false)
    const timer = setTimeout(() => finish(false), MESSAGING_TIMEOUT_MS)
    tag.addEventListener('load', onLoad)
    tag.addEventListener('error', onError)
    signal?.addEventListener('abort', onAbort, { once: true })
    try {
      if (owned) document.head.appendChild(tag)
    } catch {
      finish(false)
    }
  }).catch(() => false)
}
